// ────────────────────────────────────────────────────────────────────────────
// Stock templates + band factories.
//
// "Stratum Standard" is the current hardcoded invoice PDF
// (`features/finance/components/InvoicePdfDocument.tsx`) expressed as layout
// data — same margins, same slate palette, same column set. Keeping it as the
// shipped default means an org that never opens the builder gets exactly the
// document they get today, and it doubles as the parity test for the
// renderer: if the stock template renders differently from the old component,
// the renderer is wrong.
//
// Stock templates are read-only in the UI (`isStock`). Customising starts with
// a duplicate, so the baseline is always there to fall back to.
// ────────────────────────────────────────────────────────────────────────────
import {
  DOC_TYPES,
  LAYOUT_SCHEMA_VERSION,
  type Band,
  type BandKind,
  type DocumentLayout,
  type DocumentTemplate,
  type DocumentType,
  type LineColumn,
  type MetaField,
  type PageSettings,
  type ThemeSettings,
} from './types';

export const DEFAULT_PAGE: PageSettings = { size: 'A4', marginPt: 36 };

/** Slate palette lifted verbatim from the existing invoice PDF. */
export const DEFAULT_THEME: ThemeSettings = {
  fontFamily: 'Helvetica',
  baseFontSizePt: 9,
  textColor: '#0f172a',
  mutedColor: '#64748b',
  accentColor: '#334155',
  tableHeadColor: '#f1f5f9',
  borderColor: '#e2e8f0',
};

/**
 * Default column set. Quantity / unit price ship hidden because TMS invoice
 * lines carry a charge amount rather than priced units — an org that turns
 * them on today gets em dashes until the charge model carries them.
 */
export function defaultLineColumns(): LineColumn[] {
  return [
    // Transport columns ship HIDDEN so the stock template stays identical to
    // today's invoice PDF. They are also the columns that need the order
    // context snapshotted onto the invoice — turning one on before that lands
    // prints an empty cell, which the inspector warns about.
    {
      key: 'date',
      label: 'Date',
      visible: false,
      widthPt: 58,
      align: 'left',
      dateSource: 'collection',
      dateFormat: 'dd/MM/yyyy',
    },
    { key: 'orderNumber', label: 'Order', visible: false, widthPt: 70, align: 'left' },
    // The single prose column. Visible by default (parity with the old PDF,
    // which printed the charge description here) and zero-width, which marks it
    // as the column that fills the leftover space. `detailsMode` decides what
    // it shows once a line carries route context.
    {
      key: 'description',
      label: 'Description',
      visible: true,
      widthPt: 0,
      align: 'left',
      detailsMode: 'fromTo',
    },
    {
      key: 'orderRef',
      label: 'Order ref',
      visible: false,
      widthPt: 80,
      align: 'left',
      referenceSlot: 1,
    },
    { key: 'trailer', label: 'Trailer', visible: false, widthPt: 52, align: 'left' },
    { key: 'container', label: 'Container', visible: false, widthPt: 82, align: 'left' },
    { key: 'cargo', label: 'Cargo', visible: false, widthPt: 90, align: 'left', cargoMode: 'both' },
    // Shown only with the band's `productsDisplay` set to 'column'.
    { key: 'products', label: 'Products', visible: false, widthPt: 120, align: 'left' },
    { key: 'reference', label: 'Reference', visible: false, widthPt: 80, align: 'left' },
    { key: 'quantity', label: 'Qty', visible: false, widthPt: 40, align: 'right' },
    { key: 'unitPrice', label: 'Unit price', visible: false, widthPt: 65, align: 'right' },
    { key: 'net', label: 'Net', visible: true, widthPt: 80, align: 'right' },
    { key: 'taxRatePct', label: 'VAT %', visible: true, widthPt: 45, align: 'right' },
    { key: 'taxAmount', label: 'VAT', visible: true, widthPt: 70, align: 'right' },
    { key: 'gross', label: 'Total', visible: true, widthPt: 80, align: 'right' },
  ];
}

/** Fresh band of the given kind, for the builder's "Add band" menu. */
export function createBand(kind: BandKind, id: string): Band {
  switch (kind) {
    case 'logo':
      return { id, kind, enabled: true, spaceAfterPt: 0, align: 'left', widthPt: 120 };
    case 'title':
      return {
        id,
        kind,
        enabled: true,
        spaceAfterPt: 24,
        text: '{{doc.typeName}}',
        align: 'right',
        sizePt: 18,
        uppercase: true,
        showNumber: true,
        numberLabel: '',
        showRule: false,
      };
    case 'organisation':
      return {
        id,
        kind,
        enabled: true,
        spaceAfterPt: 18,
        align: 'left',
        fields: ['name', 'addressLines', 'taxNumber'],
      };
    case 'customer':
      return {
        id,
        kind,
        enabled: true,
        spaceAfterPt: 18,
        heading: 'Bill to',
        fields: ['name', 'code', 'addressLines'],
        addressFormat: 'oneLine',
        addressWrapChars: 38,
      };
    case 'docDetails':
      return {
        id,
        kind,
        enabled: true,
        spaceAfterPt: 18,
        heading: '',
        fields: ['issueDate', 'dueDate', 'poNumber'],
        align: 'right',
      };
    case 'lines':
      return {
        id,
        kind,
        enabled: true,
        spaceAfterPt: 12,
        columns: defaultLineColumns(),
        showHeader: true,
        zebra: false,
        groupByOrder: false,
        blankRepeatedContext: true,
      };
    case 'totals':
      return {
        id,
        kind,
        enabled: true,
        spaceAfterPt: 16,
        rows: ['subtotal', 'vatTotal', 'total'],
        showVatBreakdown: false,
        align: 'right',
        widthPt: 220,
      };
    case 'text':
      return {
        id,
        kind,
        enabled: true,
        spaceAfterPt: 12,
        heading: 'Notes',
        body: '',
        align: 'left',
        boxed: false,
      };
    case 'bank':
      return {
        id,
        kind,
        enabled: true,
        spaceAfterPt: 12,
        heading: 'Payment details',
        fields: ['accountName', 'bankName', 'iban', 'bic'],
        boxed: true,
      };
    case 'footer':
      return {
        id,
        kind,
        enabled: true,
        spaceAfterPt: 0,
        body: '{{organisation.name}} · VAT {{organisation.vatNumber}}',
        align: 'center',
        showPageNumbers: true,
        showRule: true,
      };
  }
}

/**
 * What each document kind needs, beyond the shared envelope.
 *
 * The envelope — letterhead, bill-to, line table, totals, footer — is identical
 * for all three, so this is the whole difference between them. It is expressed
 * as a band set rather than as conditions inside one layout, so that what an
 * operator sees in the builder is exactly what that kind prints: a credit-note
 * layout has no payment-details band to tick on by mistake.
 *
 * The reasons are legal, not cosmetic:
 *  - a credit note is not a demand for payment, so no due date and no bank
 *    details, and it is expected to state WHY and reference the invoice it
 *    corrects;
 *  - a pro forma is not a VAT invoice and must say so, and carries no due date
 *    because it asks for payment up front - but it does need bank details,
 *    since its whole purpose is to get paid.
 *
 * Not tax advice; the wording should be confirmed with the operator's adviser.
 */
interface DocumentKindProfile {
  /** Rows the document-details band shows. */
  metaFields: MetaField[];
  /** The free-text band, or null to ship the kind without one. */
  notes: { heading: string; body: string } | null;
  /** Whether payment details are printed. */
  bank: boolean;
  /** A fixed statement the kind must carry, printed above the notes. */
  statement: string | null;
}

const KIND_PROFILE: Record<DocumentType, DocumentKindProfile> = {
  Invoice: {
    metaFields: ['issueDate', 'dueDate', 'poNumber'],
    notes: { heading: 'Notes', body: '{{doc.message}}' },
    bank: true,
    statement: null,
  },
  CreditNote: {
    // No due date: nothing falls due on a credit note.
    metaFields: ['issueDate', 'poNumber'],
    // Ships ENABLED, unlike the invoice's optional notes — a credit note is
    // expected to say what it corrects and why, so an empty one is a fault
    // the operator should see rather than a band they must discover.
    notes: { heading: 'Reason for credit', body: '{{doc.message}}' },
    bank: false,
    statement: null,
  },
  Proforma: {
    // No due date: payment is requested up front, not on terms.
    metaFields: ['issueDate', 'poNumber'],
    notes: { heading: 'Notes', body: '{{doc.message}}' },
    // Kept: a pro forma exists to be paid, so it needs the bank block.
    bank: true,
    statement: 'This is not a VAT invoice.',
  },
};

/** The Stratum Standard layout — today's invoice PDF, as data. */
function stockLayout(docType: DocumentType): DocumentLayout {
  const profile = KIND_PROFILE[docType];
  return {
    page: { ...DEFAULT_PAGE },
    theme: { ...DEFAULT_THEME },
    bands: [
      // Logo and title share a column row, so the title sits beside the logo
      // rather than under it — which is what the old single header band did with
      // its left/right alignment slots.
      {
        id: 'band-logo',
        kind: 'logo',
        enabled: false,
        spaceAfterPt: 0,
        column: 1,
        align: 'left',
        widthPt: 120,
      },
      {
        id: 'band-title',
        kind: 'title',
        enabled: true,
        spaceAfterPt: 24,
        column: 2,
        text: '{{doc.typeName}}',
        align: 'right',
        sizePt: 18,
        uppercase: true,
        showNumber: true,
        numberLabel: '',
        showRule: false,
      },
      {
        id: 'band-organisation',
        kind: 'organisation',
        enabled: true,
        spaceAfterPt: 18,
        align: 'left',
        fields: ['name', 'addressLines', 'taxNumber'],
      },
      // Customer on the left, document details on the right — the classic
      // masthead, now two bands in two columns rather than one band splitting
      // itself.
      {
        id: 'band-customer',
        kind: 'customer',
        enabled: true,
        spaceAfterPt: 18,
        column: 1,
        heading: 'Bill to',
        fields: ['name', 'code', 'addressLines'],
        // Matches what the hardcoded invoice PDF has always printed: the stored
        // address on one wrapping line, not one line per comma.
        addressFormat: 'oneLine',
        addressWrapChars: 38,
      },
      {
        id: 'band-docdetails',
        kind: 'docDetails',
        enabled: true,
        spaceAfterPt: 18,
        column: 2,
        heading: '',
        fields: profile.metaFields,
        align: 'right',
      },
      {
        id: 'band-lines',
        kind: 'lines',
        enabled: true,
        spaceAfterPt: 12,
        columns: defaultLineColumns(),
        showHeader: true,
        zebra: false,
        groupByOrder: false,
        blankRepeatedContext: true,
      },
      {
        id: 'band-totals',
        kind: 'totals',
        enabled: true,
        spaceAfterPt: 16,
        rows: ['subtotal', 'vatTotal', 'total'],
        showVatBreakdown: false,
        align: 'right',
        widthPt: 220,
      },
      // A statement the kind is required to carry. Boxed and ahead of the
      // notes so it reads as a property of the document, not as a remark.
      ...(profile.statement
        ? [
            {
              id: 'band-statement',
              kind: 'text' as const,
              enabled: true,
              spaceAfterPt: 12,
              heading: '',
              body: profile.statement,
              align: 'left' as const,
              boxed: true,
            },
          ]
        : []),
      ...(profile.notes
        ? [
            {
              id: 'band-notes',
              kind: 'text' as const,
              enabled: true,
              spaceAfterPt: 12,
              heading: profile.notes.heading,
              body: profile.notes.body,
              align: 'left' as const,
              boxed: false,
            },
          ]
        : []),
      ...(profile.bank
        ? [
            {
              id: 'band-bank',
              kind: 'bank' as const,
              enabled: true,
              spaceAfterPt: 12,
              heading: 'Payment details',
              fields: ['accountName' as const, 'bankName' as const, 'iban' as const, 'bic' as const],
              boxed: true,
            },
          ]
        : []),
      {
        id: 'band-footer',
        kind: 'footer',
        enabled: true,
        spaceAfterPt: 0,
        body: '{{organisation.name}} · VAT {{organisation.vatNumber}}',
        align: 'center',
        showPageNumbers: true,
        showRule: true,
      },
    ],
  };
}

/**
 * Fixed ids — these are referenced as the fallback default when an org has no
 * template of its own, so they must survive a reload of the local store.
 */
export const STOCK_TEMPLATE_IDS: Record<DocumentType, string> = {
  Invoice: 'stock-invoice-standard',
  CreditNote: 'stock-credit-note-standard',
  Proforma: 'stock-proforma-standard',
};

export function buildStockTemplate(docType: DocumentType): DocumentTemplate {
  return {
    id: STOCK_TEMPLATE_IDS[docType],
    docType,
    name: 'Stratum Standard',
    isDefault: true,
    isStock: true,
    schemaVersion: LAYOUT_SCHEMA_VERSION,
    updatedUtc: '2026-08-18T00:00:00.000Z',
    layout: stockLayout(docType),
  };
}

export function buildStockTemplates(): DocumentTemplate[] {
  return DOC_TYPES.map(buildStockTemplate);
}
